import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";

/** ---------------------- Tipos ---------------------- */

type ConsumosConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;

  departamento: string;
  awaitDepartamento: string;

  title: string;
  reportTitle: string;
  fileNamePrefix: string;
};

export type SaidaItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type RetornoSolicitacaoAPI = {
  id: number | null;
  status: "solicitado" | "aprovado" | "negado" | null;
  quantidade: number | null;
  motivo: string | null;
  observacao: string | null;
  motivo_negativa: string | null;
  criado_em: string | null;
  analisado_em: string | null;
};

export type SaidaMovAPI = {
  movimentacao: {
    id: number;
    tipo?: "servico" | "venda" | "consumo" | "outro" | string | null;
    tipo_full?: string | null;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    proposta_consumo?: string | null;
    coletor_nome: string | null;
    empresa_cliente?: string | null;
    criado_em: string;
    retorno_solicitacao?: RetornoSolicitacaoAPI | null;
  };
  itens: SaidaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC";
      limit: number;
      offset: number;
    };
    count: number;
  };
  data: SaidaMovAPI[];
};

export type ConsumoRowUI = [
  string, // Técnico
  string, // Tipo
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Custo Unitário
  string, // Data
  string // Hora
];

export type RetornoStatusUI = "none" | "solicitado" | "aprovado" | "negado";

export type ConsumoRowMeta = {
  movimentacaoId: number;
  produtoId: number;
  itemId: number;

  propostaConsumo: string;

  codigo: string;
  nomeProduto: string;

  tipo: string;
  tipoFull: string;

  quantidade: number;
  custoUnitario: number;

  tecnico: string;

  responsavelId: number | null;
  responsavelNome: string | null;
  coletorNome: string | null;
  empresaCliente: string | null;

  retornoSolicitacaoId: number | null;
  retornoStatus: RetornoStatusUI;
  retornoQuantidade: number | null;
  retornoMotivo: string | null;
  retornoObservacao: string | null;
  retornoMotivoNegativa: string | null;
  retornoCriadoEm: string | null;
  retornoAnalisadoEm: string | null;
};

export type ConsumosQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

type AwaitResult = {
  changed: boolean;
  versao: number | null;
};

type Row = (string | number | React.ReactNode)[];

type ItemConsumoModal = {
  originalIndex: number;
  row: Row;
  meta: ConsumoRowMeta | null;
};

type ModalState = {
  open: boolean;
  consumoKey: string;
  tecnico: string;
  tipo: string;
  data: string;
  hora: string;
  itens: ItemConsumoModal[];
};

/** ---------------------- Constantes ---------------------- */

const API_BASE = "https://api.eva-googlemarine.com";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Configs antigas + fallback dinâmico ---------------------- */

const CONSUMOS_CONFIGS: ConsumosConfig[] = [
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-e-comercial",
    key: "suprimentos-servico-comercial",
    departamento: "suprimentos",
    awaitDepartamento: "suprimentos",
    title: "Suprimentos - Consumos do Estoque de Serviço & Comercial",
    reportTitle: "Relatório de Consumos - Suprimentos",
    fileNamePrefix: "Suprimentos-Consumos",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-campos",
    key: "suprimentos-campos",
    departamento: "suprimentos-campos",
    awaitDepartamento: "suprimentos-campos",
    title: "Suprimentos Campos - Consumos",
    reportTitle: "Relatório de Consumos - Suprimentos Campos",
    fileNamePrefix: "Suprimentos-Campos-Consumos",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-tecnicos-e-laboratorio",
    key: "tecnicos",
    departamento: "tecnicos",
    awaitDepartamento: "tecnicos",
    title: "Laboratório - Saídas como Consumo",
    reportTitle: "Relatório de Consumos - Laboratório",
    fileNamePrefix: "Laboratorio-Consumos",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-uso-coorporativo",
    key: "recursos-humanos",
    departamento: "recursos-humanos",
    awaitDepartamento: "recursos-humanos",
    title: "Suprimentos Estoque p/ Uso Coorporativo - Saídas como Consumo",
    reportTitle: "Relatório de Consumos - Estoque p/ Uso Coorporativo",
    fileNamePrefix: "Uso-Coorporativo-Consumos",
  },
];

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): ConsumosConfig {
  const area = normalizeSlug(areaSlug);
  const estoque = normalizeSlug(estoqueSlug);

  const found = CONSUMOS_CONFIGS.find(
    (config) => config.areaSlug === area && config.estoqueSlug === estoque
  );

  if (found) return found;

  const safeArea = area || "suprimentos";
  const safeEstoque = estoque || "estoque";
  const estoqueTitle = slugToTitle(safeEstoque);

  return {
    areaSlug: safeArea,
    estoqueSlug: safeEstoque,
    key: `${safeArea}-${safeEstoque}`,
    departamento: safeEstoque,
    awaitDepartamento: safeEstoque,
    title: `${slugToTitle(safeArea)} - Consumos - ${estoqueTitle}`,
    reportTitle: `Relatório de Consumos - ${estoqueTitle}`,
    fileNamePrefix: `${safeArea}-${safeEstoque}-Consumos`,
  };
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  const d = new Date(iso);

  if (!Number.isNaN(d.getTime())) {
    return {
      data: d.toLocaleDateString("pt-BR", {
        timeZone: "America/Sao_Paulo",
      }),
      hora: d.toLocaleTimeString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  }

  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );

  if (!m) {
    return {
      data: iso,
      hora: "",
    };
  }

  const [, ano, mes, dia, hora, minuto] = m;

  return {
    data: `${dia}/${mes}/${ano}`,
    hora: `${hora}:${minuto}`,
  };
}

function formatBRL(value: number): string {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseQuantidade(value: unknown): number {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function parseBRLValue(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const raw = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .trim();

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma > lastDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = raw.replace(/,/g, "");
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function normalizeRetornoStatus(
  status: RetornoSolicitacaoAPI["status"] | undefined | null
): RetornoStatusUI {
  if (status === "solicitado") return "solicitado";
  if (status === "aprovado") return "aprovado";
  if (status === "negado") return "negado";
  return "none";
}

function isConsumo(mov: SaidaMovAPI["movimentacao"]): boolean {
  const tipo = String(mov.tipo ?? "").toLowerCase();
  const tipoFull = String(mov.tipo_full ?? "").toLowerCase();

  if (tipo === "consumo") return true;
  if (tipoFull === "consumo") return true;
  if (tipoFull === "consumo-baixa") return true;
  if (tipoFull.startsWith("consumo-")) return true;
  if (tipoFull.includes("consumo")) return true;

  return false;
}

function buildConsumoKey(row: Row): string {
  const tecnico = String(row[0] ?? "").trim();
  const tipo = String(row[1] ?? "").trim();
  const data = String(row[6] ?? "").trim();
  const hora = String(row[7] ?? "").trim();

  return `${tecnico}__${tipo}__${data}__${hora}`;
}

function flattenToRows(movs: SaidaMovAPI[]): {
  rows: ConsumoRowUI[];
  rowMeta: ConsumoRowMeta[];
} {
  const rows: ConsumoRowUI[] = [];
  const rowMeta: ConsumoRowMeta[] = [];

  for (const mov of movs || []) {
    if (!isConsumo(mov.movimentacao)) continue;

    const { data, hora } = toBRT(mov.movimentacao.criado_em);

    const tecnico =
      mov.movimentacao.responsavel_nome || mov.movimentacao.coletor_nome || "-";

    const tipo =
      mov.movimentacao.tipo ??
      (mov.movimentacao.tipo_full?.startsWith("consumo-") ? "consumo" : "-");

    const tipoFull = mov.movimentacao.tipo_full || "-";
    const propostaConsumo = mov.movimentacao.proposta_consumo?.trim() || "";

    const retorno = mov.movimentacao.retorno_solicitacao || null;
    const retornoStatus = normalizeRetornoStatus(retorno?.status);

    for (const item of mov.itens || []) {
      const quantidade = Number(item.quantidade || 0);
      const custo = Number(item.custo_unitario ?? "0");

      const codigo = String(item.produto?.codigo ?? "").toUpperCase();
      const nomeProduto = String(item.produto?.nome ?? "").toUpperCase();

      rows.push([
        tecnico,
        String(tipo),
        codigo,
        nomeProduto,
        String(Number.isFinite(quantidade) ? quantidade : 0),
        `R$ ${formatBRL(Number.isFinite(custo) ? custo : 0)}`,
        data,
        hora,
      ]);

      rowMeta.push({
        movimentacaoId: mov.movimentacao.id,
        produtoId: item.produto.id,
        itemId: item.id,

        propostaConsumo,

        codigo,
        nomeProduto,

        tipo: String(tipo),
        tipoFull,

        quantidade: Number.isFinite(quantidade) ? quantidade : 0,
        custoUnitario: Number.isFinite(custo) ? custo : 0,

        tecnico,

        responsavelId: mov.movimentacao.responsavel_id,
        responsavelNome: mov.movimentacao.responsavel_nome,
        coletorNome: mov.movimentacao.coletor_nome,
        empresaCliente: mov.movimentacao.empresa_cliente ?? null,

        retornoSolicitacaoId: retorno?.id ?? null,
        retornoStatus,
        retornoQuantidade: retorno?.quantidade ?? null,
        retornoMotivo: retorno?.motivo ?? null,
        retornoObservacao: retorno?.observacao ?? null,
        retornoMotivoNegativa: retorno?.motivo_negativa ?? null,
        retornoCriadoEm: retorno?.criado_em ?? null,
        retornoAnalisadoEm: retorno?.analisado_em ?? null,
      });
    }
  }

  return {
    rows,
    rowMeta,
  };
}

function buildQuery(config: ConsumosConfig, query?: ConsumosQuery): string {
  const params = new URLSearchParams();

  params.set("estoque_slug", config.estoqueSlug);

  if (query?.date_from) params.set("date_from", query.date_from);
  if (query?.date_to) params.set("date_to", query.date_to);
  if (query?.order) params.set("order", query.order);
  if (typeof query?.limit === "number")
    params.set("limit", String(query.limit));
  if (typeof query?.offset === "number")
    params.set("offset", String(query.offset));

  const s = params.toString();

  return s ? `?${s}` : "";
}

function getConsumosUrl(config: ConsumosConfig): string {
  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.departamento
  )}/consumos`;
}

function getAwaitUrl(config: ConsumosConfig, since: number): string {
  const params = new URLSearchParams();

  params.set("since", String(since));
  params.set("timeout_ms", String(AWAIT_TIMEOUT_MS));
  params.set("estoque_slug", config.estoqueSlug);

  /**
   * Os hooks antigos variavam entre /saidas/await e alguns endpoints fixos
   * incorretos de outro departamento. Para consumo, o melhor padrão é aguardar
   * mudança em saídas, porque consumo é uma baixa de estoque.
   */
  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.awaitDepartamento
  )}/saidas/await?${params.toString()}`;
}

/** ---------------------- Fetchers ---------------------- */

async function fetchConsumosOnce(
  config: ConsumosConfig,
  query?: ConsumosQuery
): Promise<{
  rows: ConsumoRowUI[];
  rowMeta: ConsumoRowMeta[];
  meta: RespAPI["meta"] | null;
}> {
  const res = await fetch(
    `${getConsumosUrl(config)}${buildQuery(config, query)}`,
    {
      method: "GET",
      headers: buildAuthHeaders(),
      cache: "no-store",
    }
  );

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const json = (await res.json()) as RespAPI;

  if (!json.ok) {
    return {
      rows: [],
      rowMeta: [],
      meta: json.meta ?? null,
    };
  }

  const flat = flattenToRows(Array.isArray(json.data) ? json.data : []);

  return {
    rows: flat.rows,
    rowMeta: flat.rowMeta,
    meta: json.meta ?? null,
  };
}

async function awaitChange(
  config: ConsumosConfig,
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const res = await fetch(getAwaitUrl(config, since), {
      method: "GET",
      headers: buildAuthHeaders(),
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();

    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;

    const changed = Boolean(json?.changed);

    return {
      changed,
      versao,
    };
  } catch {
    return null;
  }
}

/** ---------------------- Hook genérico ---------------------- */

function useConsumosGenerico(config: ConsumosConfig, query?: ConsumosQuery) {
  const [rows, setRows] = useState<ConsumoRowUI[]>([]);
  const [rowMeta, setRowMeta] = useState<ConsumoRowMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchConsumosOnce(config, query);

      setRows(result.rows);
      setRowMeta(result.rowMeta);
      setMeta(result.meta);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar consumos.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    setLoading(true);
    setErro(null);
    setRows([]);
    setRowMeta([]);
    setMeta(null);

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const initResp = await awaitChange(config, -1);

        if (initResp && typeof initResp.versao === "number") {
          versaoRef.current = initResp.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();

            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;

            try {
              resp = await awaitChange(config, cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(jitter(ERROR_BACKOFF_MS));
              }
            } finally {
              if (awaitCtrlRef.current === ac) {
                awaitCtrlRef.current = null;
              }

              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }

              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);

            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useConsumosGenerico loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.key,
    config.areaSlug,
    config.estoqueSlug,
    config.departamento,
    JSON.stringify(query),
  ]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    rowMeta,
    loading,
    refresh,
    meta,
    erro,
  };
}

/** ---------------------- Modal styles ---------------------- */

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1080,
  background: "#ffffff",
  borderRadius: 0,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  border: "1px solid #0c2b53",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #0c2b53",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#111827",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 12px 0",
  fontSize: 14,
  fontWeight: 600,
  color: "#0c2b53",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 12,
  marginBottom: 20,
};

const fieldBoxStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  background: "#ffffff",
};

const fieldLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#0c2b53",
  marginBottom: 4,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#111827",
  fontWeight: 500,
  wordBreak: "break-word",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #0c2b53",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  background: "#ffffff",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#ffffff",
  color: "#111827",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonPrimaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#0c2b53",
  color: "#ffffff",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

/** ---------------------- Página ---------------------- */

const SaidasConsumoGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const headers = useMemo(
    () => [
      "Técnico",
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Data",
      "Hora",
    ],
    []
  );

  const columnWidths = useMemo(
    () => ["150px", "80px", "70px", "300px", "60px", "90px", "90px", "60px"],
    []
  );

  const { rows, rowMeta, loading, erro } = useConsumosGenerico(config);

  const [modal, setModal] = useState<ModalState>({
    open: false,
    consumoKey: "",
    tecnico: "",
    tipo: "",
    data: "",
    hora: "",
    itens: [],
  });

  const enabledColumnIndexes = useMemo(
    () => headers.map((_header, index) => index),
    [headers]
  );

  const totalQuantidade = useMemo(() => {
    return rows.reduce((acc, row) => acc + parseQuantidade(row[4]), 0);
  }, [rows]);

  const valorTotal = useMemo(() => {
    return rows.reduce((acc, row) => {
      const quantidade = parseQuantidade(row[4]);
      const custo = parseBRLValue(row[5]);
      return acc + quantidade * custo;
    }, 0);
  }, [rows]);

  const gerarRelatorioConsumos = useCallback(() => {
    generatePdfReport({
      reportTitle: config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Relatório de Saída de Materiais para Consumo/Uso Interno",
      headers,
      rows,
      custoColIndex: 5,
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        `Total de Itens Consumidos: ${totalQuantidade}`,
        `Valor Total dos Consumos: R$ ${valorTotal.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      ],
    });
  }, [
    config.reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    enabledColumnIndexes,
    totalQuantidade,
    valorTotal,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const base = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/saidas/consumos`
      );

      const isConsumosContext =
        currentPath.includes(base) ||
        eventPath.includes(base) ||
        currentPath.includes("consumos") ||
        eventPath.includes("consumos") ||
        moduloLabel.includes("consumos") ||
        moduloCodigo.includes("consumos") ||
        moduloSlug.includes("consumos");

      if (!isConsumosContext) return;

      gerarRelatorioConsumos();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioConsumos]);

  const openModalPorConsumo = (rowBase: Row) => {
    const key = buildConsumoKey(rowBase);

    const tecnico = String(rowBase[0] ?? "-");
    const tipo = String(rowBase[1] ?? "-");
    const data = String(rowBase[6] ?? "-");
    const hora = String(rowBase[7] ?? "-");

    const itensDoConsumo: ItemConsumoModal[] = rows
      .map((row, index) => {
        if (buildConsumoKey(row as Row) !== key) return null;

        return {
          originalIndex: index,
          row: row as Row,
          meta: rowMeta[index] ?? null,
        };
      })
      .filter(Boolean) as ItemConsumoModal[];

    setModal({
      open: true,
      consumoKey: key,
      tecnico,
      tipo,
      data,
      hora,
      itens: itensDoConsumo,
    });
  };

  const closeModal = () => {
    setModal({
      open: false,
      consumoKey: "",
      tecnico: "",
      tipo: "",
      data: "",
      hora: "",
      itens: [],
    });
  };

  const modalTotals = useMemo(() => {
    let totalItens = 0;
    let totalQuantidadeModal = 0;
    let totalCusto = 0;

    for (const item of modal.itens) {
      const quantidade = parseQuantidade(item.row[4]);
      const custo = parseBRLValue(item.row[5]);

      totalItens += 1;
      totalQuantidadeModal += quantidade;
      totalCusto += quantidade * custo;
    }

    return {
      totalItens,
      totalQuantidade: totalQuantidadeModal,
      totalCusto,
    };
  }, [modal.itens]);

  const gerarRelatorioDoModal = () => {
    if (!modal.itens.length) return;

    const modalRows = modal.itens.map((item) => item.row);

    generatePdfReport({
      reportTitle: "Relatório de Consumo",
      fileNamePrefix: `Consumo-${modal.tecnico}-${modal.data}`.replace(
        /[^\w\-]+/g,
        "_"
      ),
      subtitle: `Consumo interno - Técnico: ${modal.tecnico} | Tipo: ${modal.tipo} | Data: ${modal.data} ${modal.hora}`,
      headers,
      rows: modalRows,
      custoColIndex: 5,
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        `Itens no consumo: ${modalTotals.totalItens}`,
        `Quantidade total: ${modalTotals.totalQuantidade}`,
        `Custo total estimado: ${modalTotals.totalCusto.toLocaleString(
          "pt-BR",
          {
            style: "currency",
            currency: "BRL",
          }
        )}`,
      ],
    });
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={config.title} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando consumos…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          onDoubleClick={(viewIndex, rowData, originalIndex) => {
            const idx = originalIndex ?? viewIndex;
            const row = (rows[idx] ?? rowData) as Row;

            if (!row) return;

            openModalPorConsumo(row);
          }}
        />
      )}

      {modal.open && (
        <div style={overlayStyle} onClick={closeModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>Detalhes do consumo</h3>

                <div style={{ marginTop: 6, color: "#374151", fontSize: 14 }}>
                  <strong>{modal.tecnico}</strong> — {modal.tipo}
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              <h4 style={sectionTitleStyle}>Resumo do consumo</h4>

              <div style={gridStyle}>
                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Técnico</span>
                  <div style={fieldValueStyle}>{modal.tecnico || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Tipo</span>
                  <div style={fieldValueStyle}>{modal.tipo || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Data / Hora</span>
                  <div style={fieldValueStyle}>
                    {modal.data || "-"} {modal.hora || ""}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Itens</span>
                  <div style={fieldValueStyle}>{modalTotals.totalItens}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Quantidade total</span>
                  <div style={fieldValueStyle}>
                    {modalTotals.totalQuantidade}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Custo total estimado</span>
                  <div style={fieldValueStyle}>
                    {modalTotals.totalCusto.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </div>
                </div>
              </div>

              <h4 style={sectionTitleStyle}>Materiais</h4>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr>
                      {headers.map((header) => (
                        <th
                          key={header}
                          style={{
                            border: "1px solid #0c2b53",
                            padding: 8,
                            background: "#f8fafc",
                            color: "#0c2b53",
                            textAlign: "left",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {modal.itens.map((item) => (
                      <tr key={item.originalIndex}>
                        {headers.map((_header, index) => (
                          <td
                            key={index}
                            style={{
                              border: "1px solid #0c2b53",
                              padding: 8,
                              color: "#111827",
                            }}
                          >
                            {item.row[index] ?? "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={modalFooterStyle}>
              <button
                type="button"
                onClick={closeModal}
                style={buttonSecondaryStyle}
              >
                Fechar
              </button>

              <button
                type="button"
                onClick={gerarRelatorioDoModal}
                style={buttonPrimaryStyle}
              >
                Gerar Relatório do Consumo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaidasConsumoGenerico;
